import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SPS } from '@snoam/sps-sdk';

import type { Poster } from '@snoam/sps-sdk';
import type { ByfunnPromoArticle } from '../../../../../types/frapi-types.js';
import type { SiteVersion } from '../../../../../../core/system/rendering/types.js';

import { frontendConfig } from '../../../../../../core/system/config/frontend.server.js';
import { useEmbedHtml } from '../../../../hooks/useEmbedHtml.js';
import { usePulseContext } from '../../../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import {
  trackSalesposterView,
  trackStaticPageView,
  withByfunnPulse,
} from '../../../../../pulse/index.js';
import { fallbackPoster } from './fallback-poster.js';

export type SalesposterProps = {
  siteVersion: SiteVersion;
  id: string;
  referrer?: string;
  article?: ByfunnPromoArticle;
  refDisplayName?: string;
  articleRef: React.RefObject<HTMLDivElement | null>;
};

const getRefId = (referrer: string): string => {
  switch (referrer) {
    case 'vinkapfront':
      return 'vinkapfront';
    case 'vinklandingpage':
      return 'vinklandingpage';
    default:
      return document.referrer.includes('facebook') ? 'vink_facebook' : '';
  }
};

const Salesposter: React.FC<SalesposterProps> = ({
  id,
  referrer,
  article,
  articleRef,
}) => {
  const [poster, setPoster] = useState<Poster<'salesposter' | 'ip'> | null>(
    null,
  );

  const ref = useRef<HTMLIFrameElement>(null);

  const { registerPageLeaveTracking } = usePulseContext();

  useEffect(() => {
    if (articleRef.current) {
      registerPageLeaveTracking({
        objectElement: articleRef.current,
      });
    }
  }, [articleRef, registerPageLeaveTracking]);

  const sps = useMemo(() => {
    const { spsV3Initializer } = frontendConfig.campaignManagerConfig;

    return spsV3Initializer ? new SPS(spsV3Initializer) : null;
  }, []);

  useEffect(() => {
    if (!sps) {
      setPoster(null);

      return;
    }

    (async () => {
      try {
        const refId = referrer ? getRefId(referrer) : undefined;
        const reason = new URLSearchParams(window.location.search).get(
          'reason',
        );

        const tracking = {
          contentId: id,
          contentType: article ? ('article' as const) : ('other' as const),
          paywallType: 'paid',
          document,
          window,
        };

        const result =
          reason === 'failed-ip'
            ? await sps.getPoster('ip', {
                fallbackPoster,
                showFallbackPosterOnNoContent: true,
                tracking,
              })
            : await sps.getPoster('salesposter', {
                params: {
                  section: 'vink',
                  articleType: 'subscription',
                  refId: refId || undefined,
                },
                fallbackPoster,
                showFallbackPosterOnNoContent: true,
                tracking,
              });

        setPoster(result);
      } catch (_error) {
        setPoster(null);
      }
    })();
  }, [sps, id, referrer, article]);

  const { trackViewPage, trackViewSalesPoster } = usePulseContext();

  useEffect(() => {
    if (article && poster?.pulseOrigin) {
      trackSalesposterView(trackViewSalesPoster, article, poster.pulseOrigin);
    } else if (!article && !poster?.pulseOrigin) {
      trackStaticPageView(trackViewPage, 'bli-abonnent');
    }
  }, [trackViewPage, article, poster, trackViewSalesPoster]);

  const height = useEmbedHtml(
    ref,
    poster?.content ? { html: poster.content } : undefined,
  );

  return (
    <div className="salesposter-container">
      {poster?.content ? (
        <iframe
          ref={ref}
          title={`salesposter-${id}`}
          height={Math.min(760, height)}
          width="100%"
          style={{ border: 'none', overflowY: 'hidden' }}
          seamless={true}
          scrolling="no"
        />
      ) : (
        <div>
          Vil du lese mer? <a href="/bli-abonnent">Bli abonnent</a>
        </div>
      )}
    </div>
  );
};

const SalesposterWithPulse = withByfunnPulse(Salesposter);

export { SalesposterWithPulse as Salesposter };
